import { App } from 'antd';
import { useEffect } from 'react';
import { ApiError, normalizeApiError } from '@/shared/api/errors';
import { configureApiErrorNotifier, configureApiToastNotifier, } from '@/shared/api/httpClient';
import { reportClientError } from '@/shared/lib/error/reportClientError';
/** 同一条文案的最小重复弹出间隔，避免并发请求失败或重复提交时刷屏。 */
const DUPLICATE_WINDOW_MS = 2000;
/** 所有文案来源都取不到时使用的通用文案。 */
const FALLBACK_MESSAGE = '请求失败，请稍后重试';
/**
 * 解析最终展示文案。
 *
 * 优先级：显式文案 → 传入的已归一化文案 → 服务端文案 → 调用方兜底文案 → 通用文案。
 *
 * @param input 接口错误对象，或已归一化的错误文案。
 * @param options 文案控制。
 * @returns 可直接展示的文案。
 */
function resolveErrorMessage(input, options) {
    if (options?.message !== undefined)
        return options.message;
    if (typeof input === 'string') {
        /** 调用方传入的已归一化文案；空串视为未提供，继续走兜底。 */
        const explicitMessage = input.trim();
        return explicitMessage === '' ? FALLBACK_MESSAGE : explicitMessage;
    }
    /** 服务端给出的业务文案，只有 ApiError 才携带；空串同样视为未提供。 */
    const serverMessage = input instanceof ApiError ? normalizeApiError(input).message.trim() : '';
    if (serverMessage !== '')
        return serverMessage;
    /** 调用方兜底文案。 */
    const fallbackMessage = options?.fallback?.trim() ?? '';
    return fallbackMessage === '' ? FALLBACK_MESSAGE : fallbackMessage;
}
/**
 * 全局 toast 出口的实现，承载失败、成功、警告与信息四类提示。
 *
 * 挂在 antd App 之下以复用主题化的 message；请求层与业务代码只通过
 * `notifyApiError` / `notifyApiSuccess` / `notifyApiWarning` / `notifyApiInfo`
 * 把提示交到这里，因此全站只有一个 toast 出口：
 * 调用方不必各自 message.xxx，也不会出现同一句提示弹多次。
 */
export function ApiToastNotifier() {
    /** 主题化消息入口；用 App.useApp 而不是静态方法，避免脱离 ConfigProvider 主题。 */
    const { message } = App.useApp();
    useEffect(() => {
        /** 最近弹出的文案与时间；四个级别共用一个窗口，同一句话不会被弹第二次。 */
        const shownAt = new Map();
        /** 判断文案是否应当弹出：窗口内出现过同一文案时跳过，且不刷新已有记录。 */
        const shouldShow = (text) => {
            /** 当前时间，同时作为去重窗口的判定基准。 */
            const now = Date.now();
            for (const [shownText, shownTime] of shownAt) {
                if (now - shownTime >= DUPLICATE_WINDOW_MS)
                    shownAt.delete(shownText);
            }
            if (shownAt.has(text))
                return false;
            shownAt.set(text, now);
            return true;
        };
        configureApiErrorNotifier((input, options) => {
            /** 响应校验等非请求层失败只给通用文案：原始信息是多行 JSON，弹给创作者不可读。 */
            if (typeof input === 'object' && input !== null && !(input instanceof ApiError)) {
                reportClientError('api-response', normalizeApiError(input));
            }
            /** 最终展示文案。 */
            const text = resolveErrorMessage(input, options);
            if (!shouldShow(text))
                return;
            void message.error(text);
        });
        configureApiToastNotifier((level, text) => {
            if (!shouldShow(text))
                return;
            void message[level](text);
        });
        return () => {
            configureApiErrorNotifier(null);
            configureApiToastNotifier(null);
        };
    }, [message]);
    return null;
}
